import { Truck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { AssignRiderDialog } from "@/app/private/orders/assign-rider-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeliveryForOrder } from "@/features/delivery/hooks/use-delivery-tracking";
import { useOrderEvents } from "@/hooks/use-order-events";
import {
  type DeliveryState,
  type DeliverySummary,
  type Order,
  useAssignRider,
  useOrder,
} from "@/hooks/use-orders";
import { useRiders } from "@/hooks/use-riders";
import { formatPrice } from "@/lib/format-price";

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, error, isLoading } = useOrder(id);
  useOrderEvents(id ? { type: "order", orderId: id } : { type: "org" });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <p className="text-destructive">
          {error?.message ?? t("orders.notFound", { defaultValue: "Pedido no encontrado" })}
        </p>
        <Button asChild className="mt-3" type="button" variant="outline">
          <Link to="/orders">{t("orders.detail.back", { defaultValue: "Volver" })}</Link>
        </Button>
      </div>
    );
  }

  const { order, items } = data;
  const originLabel =
    order.origin === "public_catalog"
      ? t("orders.origin.publicCatalog", { defaultValue: "Catálogo público" })
      : t("orders.origin.qr", { defaultValue: "QR" });

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h1 className="font-semibold text-2xl">#{order.dailyNumber}</h1>
        <Button asChild type="button" variant="outline">
          <Link to="/orders">{t("orders.detail.back", { defaultValue: "Volver" })}</Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t("orders.detail.summary", { defaultValue: "Resumen" })}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{originLabel}</Badge>
            {order.origin !== "public_catalog" && order.restaurantTable?.label ? (
              <Badge variant="outline">{order.restaurantTable.label}</Badge>
            ) : null}
            <Badge variant="secondary">
              {t(`orders.status.${order.status}`, {
                defaultValue: order.status,
              })}
            </Badge>
            <Badge variant="outline">
              {t(`orders.payment.${order.paymentStatus}`, {
                defaultValue: order.paymentStatus,
              })}
            </Badge>
            <Badge>
              {order.paymentMode === "pay_now"
                ? t("orders.payment.payNow", { defaultValue: "Pagado online" })
                : t("orders.payment.payAtCounter", {
                    defaultValue: "Pago al entregar",
                  })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <h2 className="font-semibold text-sm">
              {t("orders.detail.items", { defaultValue: "Items" })}
            </h2>
            <ul className="mt-2 space-y-1">
              {items.map((item) => (
                <li className="flex items-center justify-between text-sm" key={item.id}>
                  <span>
                    {item.quantity}× {item.nameSnapshot}
                  </span>
                  <span className="tabular-nums">
                    {formatPrice(item.priceSnapshot * item.quantity, order.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-2 border-border border-t pt-2 text-sm">
            <div className="text-muted-foreground">
              {t("orders.detail.subtotal", { defaultValue: "Subtotal" })}
            </div>
            <div className="text-right tabular-nums">
              {formatPrice(order.subtotal, order.currency)}
            </div>
            <div className="text-muted-foreground">
              {t("orders.detail.discount", { defaultValue: "Descuento" })}
            </div>
            <div className="text-right tabular-nums">
              -{formatPrice(order.discountTotal, order.currency)}
            </div>
            <div className="font-semibold">
              {t("orders.detail.total", { defaultValue: "Total" })}
            </div>
            <div className="text-right font-semibold tabular-nums">
              {formatPrice(order.total, order.currency)}
            </div>
          </div>
          {order.contactName ? (
            <div className="text-sm">
              <span className="text-muted-foreground">
                {t("orders.detail.name", { defaultValue: "Nombre" })}:
              </span>{" "}
              {order.contactName}
            </div>
          ) : null}
          {order.contactPhone ? (
            <div className="text-sm">
              <span className="text-muted-foreground">
                {t("orders.detail.phone", { defaultValue: "Teléfono" })}:
              </span>{" "}
              {order.contactPhone}
            </div>
          ) : null}
          {order.notes ? <div className="rounded bg-muted p-2 text-sm">{order.notes}</div> : null}
          {order.rejectionReason ? (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-destructive text-sm">
              {order.rejectionReason}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {order.fulfillmentType === "delivery" ? <DeliverySection order={order} /> : null}
    </div>
  );
}

type DeliverySectionProps = {
  order: Order;
};

function DeliverySection({ order }: DeliverySectionProps) {
  const { t } = useTranslation();
  const { data: delivery, isLoading: deliveryLoading } = useDeliveryForOrder(order.id);
  const { data: riders } = useRiders();
  const assignMutation = useAssignRider();
  const [assignOpen, setAssignOpen] = useState(false);

  // The Delivery address snapshot is canonical for delivery Orders; the Order
  // row no longer carries a duplicated snapshot (issue #64).
  const addressSnapshot = delivery?.addressSnapshot ?? null;

  const deliveryState: DeliveryState =
    (delivery?.state as DeliveryState | undefined) ?? "unassigned";
  const riderId = delivery?.riderId ?? null;
  const assignedRider = riderId ? (riders?.find((r) => r.userId === riderId) ?? null) : null;
  const riderName = assignedRider?.name ?? null;

  const summary: DeliverySummary = {
    id: delivery?.id ?? "",
    state: deliveryState,
    riderId,
    riderName,
    enRouteAt: null,
    arrivedAt: null,
    deliveredAt: null,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Truck className="h-4 w-4" />
          {t("orders.detail.deliveryTitle", { defaultValue: "Delivery" })}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={deliveryState === "unassigned" ? "outline" : "secondary"}>
            {t(`orders.delivery.${deliveryState}`, {
              defaultValue: deliveryState.replace("_", " "),
            })}
          </Badge>
          {riderName ? (
            <Badge variant="default">{riderName}</Badge>
          ) : riderId ? (
            <Badge variant="outline">{riderId}</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {deliveryLoading && !delivery ? (
          <Skeleton className="h-16" />
        ) : (
          <>
            {delivery?.cashCollection === "not_collected" ? (
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-destructive text-sm"
                data-testid={`cash-not-collected-${order.id}`}
              >
                {t("orders.detail.cashNotCollected", {
                  defaultValue:
                    "El repartidor entregó el pedido sin cobrar el efectivo. El pago sigue pendiente.",
                })}
              </div>
            ) : null}

            {delivery?.cashCollection === "collected" ? (
              <div className="text-muted-foreground text-sm">
                {t("orders.detail.cashCollected", {
                  defaultValue: "El repartidor cobró el efectivo al entregar.",
                })}
              </div>
            ) : null}

            {addressSnapshot ? (
              <div className="space-y-1 text-sm">
                <div className="font-medium">
                  {t("orders.detail.deliveryAddress", { defaultValue: "Dirección de entrega" })}
                </div>
                <div className="text-muted-foreground">
                  {[
                    addressSnapshot.label,
                    addressSnapshot.line1,
                    addressSnapshot.line2,
                    [addressSnapshot.city, addressSnapshot.country].filter(Boolean).join(", "),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {addressSnapshot.notes ? (
                  <div className="text-muted-foreground text-xs">{addressSnapshot.notes}</div>
                ) : null}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">
                {t("orders.detail.deliveryAddressMissing", {
                  defaultValue: "Sin dirección registrada.",
                })}
              </div>
            )}

            {deliveryState === "unassigned" ? (
              <div>
                <Button
                  data-testid={`assign-rider-${order.id}`}
                  onClick={() => setAssignOpen(true)}
                  size="sm"
                  type="button"
                  variant="secondary"
                >
                  {t("orders.detail.assignRider", { defaultValue: "Asignar repartidor" })}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      <AssignRiderDialog
        assignMutation={assignMutation}
        deliverySummary={summary}
        onAssigned={() => {
          setAssignOpen(false);
        }}
        onOpenChange={setAssignOpen}
        open={assignOpen}
        orderDailyNumber={order.dailyNumber}
        orderId={order.id}
      />
    </Card>
  );
}
